import AccuroVisual from "./visuals/AccuroVisual.jsx";
import VeloDeskVisual from "./visuals/VeloDeskVisual.jsx";
import MagixDBVisual from "./visuals/MagixDBVisual.jsx";
import RecroidVisual from "./visuals/RecroidVisual.jsx";
import VeloCaptureVisual from "./visuals/VeloCaptureVisual.jsx";

/**
 * Per-product accent palette.
 * Tailwind's standard color palette isn't available in this project's config
 * (the theme replaces it), so accents are expressed as raw hex / rgba so they
 * can be applied via inline `style` from a single source of truth.
 *
 * `primary` is the main accent used for highlights/CTAs inside the showcase.
 * `light` is for fine details (sparklines, secondary glyphs).
 * `glow` is used for the section's backdrop orb on the dark background.
 * `bg` / `border` are subtle surface tints used inside the mock dashboards.
 */
const accents = {
  accuro: {
    name: "Sky Cyan",
    primary: "#22d3ee",
    light: "#a5f3fc",
    deep: "#0e7490",
    glow: "rgba(34, 211, 238, 0.28)",
    bg: "rgba(34, 211, 238, 0.10)",
    border: "rgba(34, 211, 238, 0.32)",
    softText: "rgba(165, 243, 252, 0.85)",
  },
  velodesk: {
    name: "Emerald",
    primary: "#34d399",
    light: "#a7f3d0",
    deep: "#047857",
    glow: "rgba(52, 211, 153, 0.28)",
    bg: "rgba(52, 211, 153, 0.10)",
    border: "rgba(52, 211, 153, 0.32)",
    softText: "rgba(167, 243, 208, 0.85)",
  },
  magixdb: {
    name: "Violet",
    primary: "#c084fc",
    light: "#e9d5ff",
    deep: "#6b21a8",
    glow: "rgba(192, 132, 252, 0.28)",
    bg: "rgba(192, 132, 252, 0.10)",
    border: "rgba(192, 132, 252, 0.32)",
    softText: "rgba(233, 213, 255, 0.85)",
  },
  recroid: {
    name: "Amber",
    primary: "#fbbf24",
    light: "#fde68a",
    deep: "#b45309",
    glow: "rgba(251, 191, 36, 0.28)",
    bg: "rgba(251, 191, 36, 0.10)",
    border: "rgba(251, 191, 36, 0.32)",
    softText: "rgba(253, 230, 138, 0.88)",
  },
  velocapture: {
    name: "Coral",
    primary: "#ff6b3d",
    light: "#ffbda4",
    deep: "#c2410c",
    glow: "rgba(255, 107, 61, 0.28)",
    bg: "rgba(255, 107, 61, 0.10)",
    border: "rgba(255, 107, 61, 0.34)",
    softText: "rgba(255, 189, 164, 0.88)",
  },
};

const products = [
  {
    id: "velodesk",
    name: "VeloDesk",
    tagline: "Healthcare ITSM Built for Clinical Operations",
    domain: "velodesk.velozent.com",
    logo: "/assets/images/products/velodesk.png",
    category: "Healthcare ITSM",
    description:
      "A healthcare-first IT service management platform purpose-built for hospitals, healthcare MSPs, and patient-facing operations — with AI embedded into every ticket workflow.",
    capabilities: [
      "AI ticket routing",
      "HIPAA-aware workflows",
      "Role-based portals",
      "Medical device tracking",
      "SLA intelligence",
      "Compliance & audit logging",
    ],
    accent: accents.velodesk,
    visual: VeloDeskVisual,
  },
  {
    id: "accuro-360",
    name: "Accuro 360",
    tagline: "Intelligent Identity & Access Governance",
    domain: "accuro360.velozent.com",
    logo: "/assets/images/products/accuro-360.png",
    logoAsName: true,
    category: "Identity & Access",
    description:
      "Velozent's enterprise identity and access governance platform — simplify provisioning, automate lifecycle management, and centralize access visibility across every enterprise system.",
    capabilities: [
      "Automated provisioning & deprovisioning",
      "RBAC & hierarchical access management",
      "Workflow orchestration",
      "Access lifecycle tracking",
      "Audit-ready reporting",
      "Dynamic metadata controls",
    ],
    accent: accents.accuro,
    visual: AccuroVisual,
  },
  {
    id: "magixdb",
    name: "MagixDB",
    tagline: "Ask Your Database Anything",
    domain: "magixdb.velozent.com",
    logo: "/assets/images/products/magixdb.png",
    category: "AI Analytics",
    description:
      "MagixDB converts natural-language questions into SQL queries and interactive analytics instantly, letting teams query enterprise databases using plain English.",
    capabilities: [
      "Natural language to SQL",
      "Interactive visual analytics",
      "Multi-database support",
      "Semantic table selection",
      "Intelligent caching",
      "PDF reporting",
    ],
    accent: accents.magixdb,
    visual: MagixDBVisual,
  },
  {
    id: "recroid",
    name: "Recroid",
    tagline: "Enterprise Recruitment, Reimagined",
    domain: "recroid.velozent.com",
    logo: "/assets/images/products/recroid.png",
    category: "AI Recruitment",
    description:
      "An AI-native recruitment ecosystem connecting recruiters, hiring managers, universities, and candidates within one unified hiring platform.",
    capabilities: [
      "AI candidate matching",
      "Automated applications",
      "AI resume tailoring",
      "Multi-format assessments",
      "University placement workflows",
      "Recruitment analytics",
    ],
    accent: accents.recroid,
    visual: RecroidVisual,
  },
  {
    id: "velocapture",
    name: "VeloCapture",
    tagline: "Timing Beats Targeting",
    domain: "velocapture.com",
    logo: "/assets/images/products/velocapture.svg",
    category: "Lead Gen & Bid Intelligence",
    description:
      "An AI lead-generation and government bid-intelligence platform for companies and agencies selling to U.S. government and B2B buyers. Describe what you sell, and VeloCapture finds the organizations that should buy it and the open bids you can win — ranking each by fit so you get a short, explained shortlist instead of another list to work.",
    capabilities: [
      "Plain-language company profile",
      "AI org & bid matching",
      "Fit scoring on a stable 0–100 scale",
      "Colourblind-safe A/B/C tiers",
      "Evidence linked to every signal",
      "Public + licensed data, zero list-building",
    ],
    accent: accents.velocapture,
    visual: VeloCaptureVisual,
  },
];

export { accents };
export default products;
